import { atom, useSetAtom, useAtom, useAtomValue, useStore } from 'jotai'
import { type AudioNote } from '@highlight/common'
import {
  type ChatAttachment,
  type ClipboardContents,
  type FinalizedAttachment,
  type PendingAttachment,
  type PendingAttachmentFile,
  type WindowContents,
  ChatError,
} from '../types'
import type { Skill } from '../types'
import { useCallback, useMemo } from 'react'
import { useActions } from '../providers'
import { useUploadFile } from '../hooks'
import equal from 'fast-deep-equal'
import { useIsCurrentWindowAttached, useSelectedWindows } from './atoms'
import { useAddChatToast } from './toasts'
import {
  buildLiveTranscriptText,
  LIVE_TRANSCRIPT_PENDING_KEY,
  mapTranscriptEntriesToTranscriptString,
} from '../utils/audioNotes'
import { getActiveTranscript } from '@renderer/hooks/audio/use-audio-transcript'
import { useGetRealChatId } from './chats'

export const MAX_NUMBER_OF_ATTACHMENTS = 8

/**
 * By live meeting note id, the chat that last received its transcript and the
 * timestamp of the last entry sent, so a follow-up in that chat sends only
 * what's new. In memory only. Losing it costs one full resend, but a cursor
 * that outlived its thread would skip transcript the model never saw.
 */
const liveTranscriptCursorsAtom = atom<Record<string, { chatId: string; lastEntryAt: string }>>({})

export interface ClearAttachmentsOptions {
  /** Leave `viewScoped` attachments: the clear is about the chat, not the open view. */
  keepViewScoped?: boolean
}

interface AddAudioNoteOptions {
  /**
   * Tell the user when the note turns out to have no transcript. Only for an
   * explicit attach: views that auto-attach their note as context (the Meeting
   * View) would otherwise raise it on every open of an empty meeting.
   */
  reportMissingTranscript?: boolean
  /** See `ChatAttachment.viewScoped`. */
  viewScoped?: boolean
}

/**
 * Resolve a duplicate audio note: the existing copy wins, unless the incoming
 * one is view-scoped. A view's copy must take over, or a chat-level clear would
 * drop the unflagged copy and the view would be left with no chip.
 */
function keepOneAudioNote(
  prev: Array<ChatAttachment>,
  isSameNote: (attachment: ChatAttachment) => boolean,
  incoming: ChatAttachment,
): Array<ChatAttachment> {
  if (!incoming.viewScoped) return prev
  // A pending and a finalized copy can both match; the incoming one replaces the first.
  let placed = false
  return prev.flatMap((attachment) => {
    if (!isSameNote(attachment)) return [attachment]
    if (placed) return []
    placed = true
    return [incoming]
  })
}

/** Notes whose transcript fetch is running; re-attaches while one is in flight reuse it. */
const audioNoteFetchesInFlight = new Set<string>()

/**
 * Audio transcription caches for audio notes.
 */
const audioTranscriptCacheAtom = atom<AudioNote[]>([])
export const useAudioTranscriptCache = () => useAtomValue(audioTranscriptCacheAtom)
export const useSetAudioTranscriptCache = () => useSetAtom(audioTranscriptCacheAtom)

/**
 * Attachments uploading state.
 */
const isUploadingAtom = atom(false)
export const useAttachmentUploading = () => {
  const [isUploading, setUploading] = useAtom(isUploadingAtom)
  return {
    isAttachmentUploading: isUploading,
    setAttachmentUploading: setUploading,
  }
}

export const useSetAttachments = () => useSetAtom(attachmentsAtom)

/**
 * Input attachments store
 */
const attachmentsAtom = atom<ChatAttachment[]>([])
export const useAttachmentStore = () => {
  const store = useStore()
  const setAttachments = useSetAttachments()
  const cachedTranscripts = useAudioTranscriptCache()
  const setConversationTranscript = useSetAudioTranscriptCache()
  const { getConversation } = useActions()
  const addChatToast = useAddChatToast()

  const addAttachment = useCallback(
    (attachment: ChatAttachment) => {
      setAttachments((prev) => {
        if (attachment.attachment?.type === 'clipboardText' && attachment.attachment.text) {
          // De-dupe clipboard text
          const clipboardText = attachment.attachment.text
          const clipboardAttachments = prev.filter((a) => a.attachment && a.attachment.type === 'clipboardText')
          if (
            clipboardAttachments.some(
              (a) => a.attachment?.type === 'clipboardText' && a.attachment?.text === clipboardText,
            )
          ) {
            return prev
          }
        } else if (attachment.pending?.type === 'image' && attachment.pending.body) {
          // De-dupe image URLs
          const imageUrl = attachment.pending.body
          const imageAttachments = prev.filter((a) => a.pending && a.pending.type === 'image')
          if (imageAttachments.some((a) => a.pending?.type === 'image' && a.pending?.body === imageUrl)) {
            return prev
          }
        } else if (attachment.pending?.type === 'audioNote') {
          // De-dupe a pending audio note against pending or finalized copies of the same note
          const conversationId = attachment.pending.conversationId
          const isSameNote = (a: ChatAttachment) =>
            (a.pending?.type === 'audioNote' && a.pending.conversationId === conversationId) ||
            (a.attachment?.type === 'audioNote' && a.metadata?.id === conversationId)
          if (prev.some(isSameNote)) {
            return keepOneAudioNote(prev, isSameNote, attachment)
          }
        } else if (attachment.attachment?.type === 'audioNote' && attachment.metadata?.id) {
          // De-dupe audio notes by conversation ID
          const isSameNote = (a: ChatAttachment) =>
            a.attachment?.type === 'audioNote' && a.metadata?.id === attachment.metadata?.id
          if (prev.some(isSameNote)) {
            return keepOneAudioNote(prev, isSameNote, attachment)
          }
        } else if (
          attachment.attachment?.type === 'insight' &&
          attachment.attachment.insightId &&
          prev.some(
            (a) =>
              a.attachment?.type === 'insight' &&
              a.attachment.insightId ===
                (attachment.attachment! as Extract<FinalizedAttachment, { type: 'insight' }>).insightId,
          )
        ) {
          return prev
        }
        return [...prev, attachment]
      })
    },
    [setAttachments],
  )

  const removeAttachment = useCallback(
    (attachment: number | ChatAttachment) => {
      setAttachments((prev) => {
        let attachmentIndex: number
        if (typeof attachment === 'number') {
          attachmentIndex = attachment
        } else {
          attachmentIndex = prev.findIndex((att) => equal(att, attachment))
        }
        return prev.filter((_, index) => index !== attachmentIndex)
      })
    },
    [setAttachments],
  )

  const removeAttachments = useCallback(
    (filter: (attachment: ChatAttachment) => boolean) => {
      setAttachments((prev) => {
        return prev.filter(filter)
      })
    },
    [setAttachments],
  )

  /** Returns the attachments it dropped. */
  const clearAttachments = useCallback(
    (options?: ClearAttachmentsOptions): Array<ChatAttachment> => {
      const attachments = store.get(attachmentsAtom)
      const isKept = (attachment: ChatAttachment) => !!options?.keepViewScoped && !!attachment.viewScoped
      setAttachments(attachments.filter(isKept))
      return attachments.filter((attachment) => !isKept(attachment))
    },
    [store, setAttachments],
  )

  const addClipboardAttachment = useCallback(
    (clipboardContents: ClipboardContents) => {
      if (clipboardContents?.type === 'text') {
        addAttachment({
          attachment: {
            type: 'clipboardText',
            text: clipboardContents.value,
          },
        })
      } else if (clipboardContents?.type === 'image') {
        addAttachment({
          pending: {
            type: 'image',
            body: clipboardContents.value, // pending attachments use body
          },
        })
      }
    },
    [addAttachment],
  )

  const addFileAttachment = useCallback(
    (file: File, value: string | ArrayBuffer) => {
      addAttachment({
        pending: {
          type: 'file',
          body: value,
          name: file.name,
          mimeType: file.type,
        },
      })
    },
    [addAttachment],
  )

  const addScreenshotAttachment = useCallback(
    (imageUrl: string, fileName?: string) => {
      addAttachment({
        pending: {
          type: 'image',
          body: imageUrl,
          name: fileName,
        },
      })
    },
    [addAttachment],
  )

  const fetchAndFinalizeAudioNote = useCallback(
    async (conversationId: string, { reportMissingTranscript = false }: AddAudioNoteOptions = {}) => {
      if (audioNoteFetchesInFlight.has(conversationId)) {
        return
      }
      audioNoteFetchesInFlight.add(conversationId)
      try {
        const updatedConversation = await getConversation(conversationId)

        if (!updatedConversation?.transcriptEntries?.length) {
          // remove the pending attachment
          setAttachments((prev) =>
            prev.filter(
              (att) =>
                !(
                  att.pending?.type === 'audioNote' &&
                  (att.pending as Extract<PendingAttachment, { type: 'audioNote' }>).conversationId === conversationId
                ),
            ),
          )
          if (reportMissingTranscript) {
            addChatToast({
              title: 'No transcript found',
              message: `The transcript for “${updatedConversation?.title ?? 'this audio note'}” is not available.`,
              type: 'error',
            })
          }
          return
        }

        if (updatedConversation && updatedConversation.transcriptEntries?.length) {
          // Update the cache
          setConversationTranscript((prev) => {
            const existingIndex = prev.findIndex((t) => t.id === conversationId)
            if (existingIndex !== -1) {
              // Replace existing entry
              return prev.map((item, index) => (index === existingIndex ? updatedConversation : item))
            } else {
              // Add new entry
              return [...prev, updatedConversation]
            }
          })

          // Finalize the pending audio note attachment by conversationId instead of index
          setAttachments((prevAttachments) => {
            const result = prevAttachments.map((att, index) => {
              if (
                att.pending?.type === 'audioNote' &&
                (att.pending as Extract<PendingAttachment, { type: 'audioNote' }>).conversationId === conversationId
              ) {
                const pendingAudioNote = att.pending as Extract<PendingAttachment, { type: 'audioNote' }>
                const transcript = mapTranscriptEntriesToTranscriptString(updatedConversation.transcriptEntries)
                return {
                  viewScoped: att.viewScoped,
                  attachment: {
                    type: 'audioNote',
                    title: pendingAudioNote.title ?? updatedConversation.title ?? 'Audio Notes',
                    startTime: pendingAudioNote.startTime,
                    text: transcript,
                  },
                  metadata: {
                    ...updatedConversation,
                    transcript,
                  },
                } as ChatAttachment
              }
              return att
            })
            return result
          })
        }
      } catch (error) {
        // Find and remove the failed attachment by conversationId
        setAttachments((prev) =>
          prev.filter(
            (att) =>
              !(
                att.pending?.type === 'audioNote' &&
                (att.pending as Extract<PendingAttachment, { type: 'audioNote' }>).conversationId === conversationId
              ),
          ),
        )
      } finally {
        audioNoteFetchesInFlight.delete(conversationId)
      }
    },
    [getConversation, setConversationTranscript, setAttachments, addChatToast],
  )

  const addAudioNoteAttachment = useCallback(
    (audioNote: AudioNote, options?: AddAudioNoteOptions) => {
      // Check if audio transcription is cached for this conversation
      const cachedTranscript = mapTranscriptEntriesToTranscriptString(
        cachedTranscripts.find((t) => t.id === audioNote.id)?.transcriptEntries,
      )

      // Use cached transcript if available, otherwise fall back to audioNote.transcript
      const transcriptText = cachedTranscript ?? mapTranscriptEntriesToTranscriptString(audioNote.transcriptEntries)

      if (transcriptText) {
        // If we have a transcript, create finalized attachment
        addAttachment({
          viewScoped: options?.viewScoped,
          attachment: {
            type: 'audioNote',
            title: audioNote.title ?? 'Audio Notes',
            startTime: (audioNote.startedAt instanceof Date
              ? audioNote.startedAt
              : new Date(audioNote.startedAt)
            ).toISOString(),
            text: transcriptText,
          },
          metadata: {
            ...audioNote,
            transcript: transcriptText,
          },
        })
      } else {
        // If no transcript, create pending attachment and trigger fetch
        const startTime = (
          audioNote.startedAt instanceof Date ? audioNote.startedAt : new Date(audioNote.startedAt)
        ).toISOString()

        addAttachment({
          viewScoped: options?.viewScoped,
          pending: {
            type: 'audioNote',
            conversationId: audioNote.id,
            title: audioNote.title ?? 'Audio Notes',
            startTime,
          },
          metadata: audioNote,
        })
        fetchAndFinalizeAudioNote(audioNote.id, options)
      }
    },
    [addAttachment, cachedTranscripts, fetchAndFinalizeAudioNote],
  )

  const addWindowContentsAttachment = useCallback(
    (windowContents: Exclude<WindowContents, { type: 'windowContents' }>) => {
      addAttachment({
        attachment: {
          type: 'windowContents',
          createdAt: new Date().toISOString(),
          ...windowContents,
        },
      })
    },
    [addAttachment],
  )

  const addSelectedTextAttachment = useCallback(
    (selectedText: string) => {
      addAttachment({
        attachment: {
          type: 'selectedText',
          text: selectedText,
        },
      })
    },
    [addAttachment],
  )

  const addOCRTextAttachment = useCallback(
    (ocrText: string) => {
      addAttachment({
        attachment: {
          type: 'ocr',
          text: ocrText,
        },
      })
    },
    [addAttachment],
  )

  const addQuoteReferenceAttachment = useCallback(
    (quoteText: string) => {
      addAttachment({
        attachment: {
          type: 'quoteReference',
          text: quoteText,
        },
      })
    },
    [addAttachment],
  )

  const addInsightAttachments = useCallback(
    (...insightIds: string[]) => {
      for (const insightId of insightIds) {
        addAttachment({
          attachment: {
            type: 'insight',
            insightId: insightId,
          },
        })
      }
    },
    [addAttachment],
  )

  const addSkillAttachment = useCallback(
    (skill: Skill) => {
      // De-dupe by skillId
      setAttachments((prev) => {
        if (prev.some((a) => a.pending?.type === 'skill' && a.pending.skillId === skill.id)) {
          return prev
        }
        return [
          ...prev,
          {
            pending: {
              type: 'skill' as const,
              skillId: skill.id,
              name: skill.name,
              description: skill.description,
              toolset: skill.toolset,
            },
          },
        ]
      })
    },
    [setAttachments],
  )

  return useMemo(() => {
    return {
      setAttachments,
      addAttachment,
      removeAttachment,
      removeAttachments,
      clearAttachments,
      addAudioNoteAttachment,
      addClipboardAttachment,
      addFileAttachment,
      addScreenshotAttachment,
      addWindowContentsAttachment,
      addSelectedTextAttachment,
      addQuoteReferenceAttachment,
      addOCRTextAttachment,
      addInsightAttachments,
      addSkillAttachment,
      fetchAndFinalizeAudioNote,
    }
  }, [
    setAttachments,
    addAttachment,
    removeAttachment,
    clearAttachments,
    addAudioNoteAttachment,
    addClipboardAttachment,
    addFileAttachment,
    addScreenshotAttachment,
    addWindowContentsAttachment,
    addSelectedTextAttachment,
    addQuoteReferenceAttachment,
    addOCRTextAttachment,
    addInsightAttachments,
    addSkillAttachment,
    fetchAndFinalizeAudioNote,
  ])
}

export const useAttachments = () => {
  return useAtomValue(attachmentsAtom)
}

// Helper function to create standardized error objects
function createStandardError(err: unknown, context: string): Error {
  console.error(`Error ${context}:`, err)

  if (err instanceof Error) {
    return err
  }

  const errorMsg = typeof err === 'object' ? (err ? JSON.stringify(err) : 'null') : String(err)

  return new Error(`${context}: ${errorMsg}`)
}

export const useFinalizeAttachments = () => {
  const { compressBase64Image } = useActions()
  const { setAttachmentUploading } = useAttachmentUploading()
  const uploadFile = useUploadFile()
  const store = useStore()
  const getRealChatId = useGetRealChatId()

  return async (
    attachments: ChatAttachment[],
    chatId?: string,
  ): Promise<{ attachedContext: FinalizedAttachment[]; error?: ChatError; onTurnAccepted?: () => void }> => {
    const finalized: FinalizedAttachment[] = []
    const pending: Promise<FinalizedAttachment>[] = []
    let uploadError: ChatError | undefined
    // Moved only once the backend takes the turn: a send that fails must leave its lines for the next one.
    const sentCursors: Record<string, { chatId: string; lastEntryAt: string }> = {}

    for (let index = 0; index < attachments.length; index++) {
      const attachment = attachments[index]
      if (attachment.pending) {
        pending.push(
          new Promise<FinalizedAttachment>(async (resolve, reject) => {
            console.log('pending attachment:', attachment.pending?.type)
            switch (attachment.pending!.type) {
              case 'image': {
                const pending = attachment.pending
                if (typeof pending.body !== 'string') {
                  const err = new Error('Image body is not a string')
                  uploadError = err as ChatError
                  reject(err)
                  return
                }

                let base64Image
                try {
                  base64Image = await compressBase64Image(pending.body, {
                    format: 'jpg',
                    quality: 80,
                  })
                } catch (err: unknown) {
                  const errorObj = createStandardError(err, 'compressing image')
                  uploadError = errorObj as ChatError
                  reject(errorObj)
                  return
                }

                const { data, mimeType } = base64ToUint8Array(base64Image)

                // for inline images, cap at ~1.4 MB to stay under the ~2 MiB durable-object cell limit
                const isOverInlineLimit = base64Image.length > 1.4 * 1024 * 1024

                if (isOverInlineLimit) {
                  try {
                    const result = await uploadFile({
                      name: attachment.pending?.name,
                      mimeType,
                      data,
                    })
                    const finalized: FinalizedAttachment = {
                      type: 'image',
                      fileName: attachment.pending?.name,
                      fileId: result,
                    }
                    return resolve(finalized)
                  } catch (err: unknown) {
                    const errorObj = createStandardError(err, 'uploading image')
                    uploadError = errorObj as ChatError
                    reject(errorObj)
                    return
                  }
                }

                const finalized: FinalizedAttachment = {
                  type: 'image',
                  fileName: attachment.pending?.name,
                  fileDataUrl: base64Image,
                }
                return resolve(finalized)
              }
              case 'file': {
                const pending = attachment.pending as PendingAttachmentFile

                try {
                  let data: Uint8Array
                  // This type is wrong, so do a check anyways
                  if (typeof pending.body === 'string' && isDataURL(pending.body)) {
                    console.log('Converting pending attachment body from base64 to Uint8Array')
                    data = base64ToUint8Array(pending.body).data
                  } else if (typeof pending.body === 'string') {
                    console.log('Uploading pending attachment as plaintext')
                    const finalized: FinalizedAttachment = {
                      type: 'textFile',
                      contents: pending.body,
                    }
                    return resolve(finalized)
                  } else if (pending.body instanceof ArrayBuffer) {
                    data = new Uint8Array(pending.body)
                  } else {
                    console.log('Using raw pending attachment body')
                    data = pending.body
                  }

                  if (!pending.mimeType) {
                    const err = new Error('No mime type provided for file attachment')
                    uploadError = err as ChatError
                    reject(err)
                    return
                  }

                  if (pending.mimeType === 'application/pdf') {
                    // Upload the file
                    const fileId = await uploadFile({
                      mimeType: pending.mimeType,
                      data,
                      name: pending.name,
                    })

                    const finalized: FinalizedAttachment = {
                      type: 'pdf',
                      fileId: fileId,
                      fileName: pending.name,
                    }
                    return resolve(finalized)
                  } else if (pending.mimeType?.startsWith('image/')) {
                    // for inline images, cap at ~1.4 MB to stay under the ~2 MiB durable-object cell limit
                    const isStringOverInlineLimit =
                      typeof pending.body === 'string' && pending.body.length > 1.4 * 1024 * 1024

                    if (isStringOverInlineLimit || typeof pending.body !== 'string') {
                      try {
                        const result = await uploadFile({
                          name: pending.name,
                          mimeType: pending.mimeType,
                          data,
                        })

                        const finalized: FinalizedAttachment = {
                          type: 'image',
                          fileName: pending.name,
                          fileId: result,
                        }

                        return resolve(finalized)
                      } catch (err: unknown) {
                        const errorObj = createStandardError(err, 'uploading image')
                        uploadError = errorObj as ChatError
                        reject(errorObj)
                        return
                      }
                    }

                    const finalized: FinalizedAttachment = {
                      type: 'image',
                      fileName: pending.name,
                      fileDataUrl: pending.body,
                    }

                    return resolve(finalized)
                  } else {
                    const err = new ChatError(`Unrecognized mimeType: ${pending.mimeType}`, {
                      chatId: 'upload-file',
                      userMessageId: null,
                      assistantMessageId: null,
                      errorCode: 'unrecognized-mime-type',
                    })
                    console.error('Unrecognized mimeType:', pending.mimeType)
                    uploadError = err
                    reject(err)
                    return
                  }
                } catch (err: unknown) {
                  const errorObj = createStandardError(err, 'processing file attachment')
                  uploadError = errorObj as ChatError
                  reject(errorObj)
                  return
                }
              }
              case 'skill': {
                const pending = attachment.pending!
                if (pending.type !== 'skill') return
                return resolve({
                  type: 'skill',
                  skillId: pending.skillId,
                  name: pending.name,
                } as unknown as FinalizedAttachment)
              }
              default: {
                const err = new Error(
                  `Unsupported pending attachment type: ${(attachment.pending as { type: string }).type}`,
                )
                uploadError = err as ChatError
                reject(err)
                return
              }
            }
          }),
        )
      } else if (attachment.attachment?.type === 'audioNote' && attachment.metadata?.[LIVE_TRANSCRIPT_PENDING_KEY]) {
        // Live meeting chips ship empty text so the Sidepanel doesn't rebuild the
        // full transcript string on every ASR delta — fill once at send.
        const liveChip = attachment.attachment
        const noteId = attachment.metadata.id as string | undefined
        pending.push(
          (async (): Promise<FinalizedAttachment> => {
            const { entries } = await getActiveTranscript()
            // A new chat id is a temp one until the stream renames it, so compare resolved ids.
            const cursor = noteId ? store.get(liveTranscriptCursorsAtom)[noteId] : undefined
            const sentThrough =
              cursor && chatId && getRealChatId(cursor.chatId) === getRealChatId(chatId)
                ? cursor.lastEntryAt
                : undefined
            const lastEntryAt = entries.at(-1)?.timestamp.absolute
            if (noteId && chatId && lastEntryAt) {
              sentCursors[noteId] = { chatId, lastEntryAt }
            }
            return {
              ...liveChip,
              type: 'audioNote',
              text: buildLiveTranscriptText(entries, sentThrough),
            }
          })(),
        )
      } else {
        finalized.push(attachment.attachment)
      }
    }

    setAttachmentUploading(true)
    const settled = await Promise.allSettled<FinalizedAttachment>(pending)
    setAttachmentUploading(false)

    // Check for any errors in the settled promises
    const rejectedPromises = settled.filter((result) => result.status === 'rejected')

    // If there are rejected promises, log them concisely
    if (rejectedPromises.length > 0) {
      console.error(`${rejectedPromises.length} attachment upload(s) failed`)

      // If we don't already have an error, use the first rejection reason
      if (!uploadError && rejectedPromises[0].status === 'rejected') {
        const reason = rejectedPromises[0].reason
        uploadError = reason instanceof Error ? (reason as ChatError) : (new Error(String(reason)) as ChatError)
      }
    }

    // Collect successful uploads
    for (const final of settled) {
      if (final.status === 'fulfilled') {
        finalized.push(final.value)
      }
    }

    const onTurnAccepted =
      Object.keys(sentCursors).length > 0
        ? () => store.set(liveTranscriptCursorsAtom, (prev) => ({ ...prev, ...sentCursors }))
        : undefined
    return { attachedContext: finalized, error: uploadError, onTurnAccepted }
  }
}

function base64ToUint8Array(base64: string): { data: Uint8Array; mimeType: string } {
  // Split the base64 string to remove the "data:mime/type;base64," part
  const parts = base64.split(',')
  // Extract the mime type from the data URL
  const mimeType = parts[0].match(/data:(.*?);/)?.[1] || ''
  // Extract the actual base64 data
  const base64Data = parts[1]

  // Decode base64 to raw binary string
  const rawData = atob(base64Data)

  // Create a Uint8Array from the raw binary string
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return {
    data: outputArray,
    mimeType,
  }
}

export const useRemainingAttachments = () => {
  const attachments = useAttachments()
  const selectedWindows = useSelectedWindows()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const totalAttachments = attachments.length + selectedWindows.length + (isCurrentWindowAttached ? 1 : 0)
  return Math.max(MAX_NUMBER_OF_ATTACHMENTS - totalAttachments, 0)
}

export const useIsMaxAttachmentsReached = () => {
  const remainingAttachments = useRemainingAttachments()
  return remainingAttachments === 0
}

const dataUrlRegex =
  /^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i
function isDataURL(s: string) {
  return !!s.match(dataUrlRegex)
}
